import React, { useEffect, useState } from "react";
import { PageHeader } from "@ant-design/pro-components";
import {
    Layout,
    Breadcrumb,
    Row,
    Col,
    Table,
    Space,
    Modal,
    Tag,
    Input,
    Button,
    ConfigProvider,
} from "antd";
import ptBRIntl from "antd/lib/locale/pt_BR";
import {
    ExclamationCircleOutlined,
    PlusCircleOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import "reactjs-popup/dist/index.css";

/**
 * Actions
 */
import { allCategoriesObj, removeCategoryObj } from "auth/actions/objActions";

/**
 * Utils
 */
import { defaultTablePagination } from "utils/table-pagination";
import { hexToRgba } from "utils/color";

/**
 * Components
 */
import TopBar from "components/NavBar/TopBar";
import DrawerSider from "components/NavBar/DrawerSider";
import { LayoutTopSide } from "components/NavBar/styles";
import { useSelector } from "react-redux";
import { getAllPermissionsByUnit, hasAnyPermission } from "../../../../utils/permission";
import PermissionWrapperComponent from "../../../../components/PermissionWrapperComponent";

/**
 * Misc
 */
const { Content } = Layout;
const { confirm } = Modal;

/**
 * Arrays para query params e localStorage
 */
const NAME_FILTERS_LOCAL_STORAGE = {
    CATEGORY: "selectedCategory",

}

const NAME_QUERY_PARAMS = {
    CATEGORY: "category",

}

function AllFormCategories() {
    const user = useSelector((state) => state.session.user);
    const permissions = useSelector((state) => state.user.permissions);
    const company = useSelector((state) => state.user.company);

    const navigate = useNavigate();
    const location = useLocation();

    const urlParams = new URLSearchParams(location.search);
    const inititalFilter = urlParams.get(NAME_QUERY_PARAMS.CATEGORY) || localStorage.getItem(NAME_FILTERS_LOCAL_STORAGE.CATEGORY) || "";

    const [categories, setCategories] = useState([]);
    const [auxCategory, setAuxCategory] = useState("");
    const [filtro, setFiltro] = useState(inititalFilter);
    const [permissionEntity, setPermissionEntity] = useState(null);


    useEffect(() => {
        allCategoriesObj({
            companyId: company.companyId,
            unitId: company.unitId
        }).then((items) => {
            const permissionEntityData = getAllPermissionsByUnit(permissions, company.unitId, ['UPDATE_CATEGORY', 'DELETE_CATEGORY']);
            setPermissionEntity(permissionEntityData);
            const permissionEntityById = permissionEntityData.map(item => item.context.entity_id)
            const data = items.message.filter(item => permissionEntityById.includes(item._id))
            setAuxCategory(data);
            setCategories(data);
        });
    }, [company]);

    // UseEffect para aplicar o filtro
    useEffect(() => {
        const currentUrlName = urlParams.get(NAME_QUERY_PARAMS.CATEGORY) || "";

        if (filtro !== currentUrlName) {
            updateUrlAndStorage(filtro);
        }

        // Se o filtro mudou no estado, aplica-o aos dados
        if (auxCategory.length > 0) {
            applyFilter(auxCategory, filtro);
        }

    }, [filtro, auxCategory]);

    // Função responsável por atualizar query params/localStorage
    const updateUrlAndStorage = (newFiltro) => {
        const newUrlParams = new URLSearchParams(location.search);

        if (newFiltro) {
            newUrlParams.set(NAME_QUERY_PARAMS.CATEGORY, newFiltro);
            localStorage.setItem(NAME_FILTERS_LOCAL_STORAGE.CATEGORY, newFiltro);
        } else {
            newUrlParams.delete(NAME_QUERY_PARAMS.CATEGORY);
            localStorage.removeItem(NAME_FILTERS_LOCAL_STORAGE.CATEGORY);
        }

        navigate({
            pathname: location.pathname,
            search: newUrlParams.toString(),
        });
    };

    const applyFilter = (dataToFilter, searchString) => {
        const currValue = searchString.toLowerCase();

        if (currValue === "") {
            setCategories(dataToFilter);
        } else {
            const filteredData = dataToFilter.filter(
                (entry) => entry.name.toLowerCase().includes(currValue)
            );
            setCategories(filteredData);
        }
    };

    const handleSearchChange = (pesquisa) => {
        setFiltro(pesquisa);
    };

    useEffect(() => {
        const data = location.state;
        if (data) {
            setCategories(data.categories);
        }
    }, [location.state, categories]);


    const routes = [
        {
            breadcrumbName: "Categorias",
            href: "/formularios/categoria/todas",
        },
        {
            breadcrumbName: "todas",
        },
    ];
    const columns = [
        {
            title: "Nome",
            dataIndex: "name",
            key: "name",
        },
        {
            title: "Cor",
            dataIndex: "color",
            key: "color",
            render: (record) => {
                const hex = record !== undefined ? record.hex : "#03A9F4";
                // Fundo suave na cor da categoria (mesmo padrão da lista de
                // formulários), para não estourar o contraste no modo escuro
                return (
                    <Tag
                        style={{
                            backgroundColor: hexToRgba(hex, 0.15),
                            borderColor: hexToRgba(hex, 0.4),
                            color: hex,
                        }}
                    >
                        {hex.toUpperCase()}
                    </Tag>
                );
            },
        },
        {
            title: "Ações",
            key: "action",
            render: (text, record) => {
                const permissions = permissionEntity.filter(item => item.context.entity_id === record._id);
                return (
                    <Space size="middle">

                        {hasAnyPermission(permissions, ['UPDATE_CATEGORY'], company.unitId) && (
                            <a href onClick={() => handleRequest(record)}>
                                Editar
                            </a>)}
                        {hasAnyPermission(permissions, ['DELETE_CATEGORY'], company.unitId) && (
                            <a href onClick={() => handleDelete(record)}>
                                Remover
                            </a>)}

                    </Space>
                );
            },
        },
    ];

    /**
     * Redericiona para todas categorias
     * @param {*} info
     */
    const handleRequest = (info) => {
        navigate(`/formularios/categoria/editar/${info._id}`);
    };

    const newCategory = () => {
        navigate({
            pathname: `/formularios/categoria/nova`,
        });
    };

    /**
     * Remove categoria
     * @param {*} record
     */
    function handleDelete(record) {
        confirm({
            title: `Você realmente deseja excluir categoria?`,
            icon: <ExclamationCircleOutlined />,
            content: "Essa ação não pode ser desfeita.",
            okText: "Confirmar",
            onOk() {
                removeCategoryObj({ ...record, deletedBy: user.sub }).then((res) => {
                    window.location.reload();
                });
            },
            cancelText: "Cancelar",
        });
    }

    return (
        <LayoutTopSide>
            <TopBar />
            <Layout style={{ overflow: "hidden" }}>
                <DrawerSider />
                <Layout style={{ padding: "0 24px 24px", height: "100%", overflowY: "auto" }}>
                        <PageHeader
                            style={{ margin: "16px 0 5px 0" }}
                            title="Lista de categorias"
                            subTitle="As categorias são utilizadas para classificar e organizar seus formulários."
                            breadcrumb={
                                <Breadcrumb>
                                    {routes.map((a) =>
                                        a.hasOwnProperty("href") ? (
                                            <Breadcrumb.Item key={a.breadcrumbName}>
                                                <a href onClick={() => navigate(a.href)}>
                                                    {a.breadcrumbName}
                                                </a>
                                            </Breadcrumb.Item>
                                        ) : (
                                            <Breadcrumb.Item key={a.breadcrumbName}>{a.breadcrumbName}</Breadcrumb.Item>
                                        )
                                    )}
                                </Breadcrumb>
                            }
                        />
                        <Content
                            className="site-layout-background"
                            style={{
                                padding: 24,
                                margin: 0,
                                minHeight: "fit-content",
                                background: "var(--cp-surface)",
                            }}
                        >
                            <Row gutter={[16, 8]} justify="end">
                                <Col xs={24} sm={12} md={10}>
                                    <Input
                                        placeholder="Pesquisar por nome"
                                        value={filtro}
                                        onChange={(e) => {
                                            handleSearchChange(e.target.value);
                                        }}
                                    />
                                </Col>
                                <Col xs={24} sm={12} md={{ span: 7 }} xl={{ span: 6 }} xxl={{ span: 4 }}>
                                    <PermissionWrapperComponent permissions={['CREATE_CATEGORY']}>
                                        <div
                                            style={{
                                                marginBottom: 16,
                                            }}
                                        >
                                            <Button
                                                type="primary"
                                                onClick={newCategory}
                                                style={{ width: "100%" }}
                                            >
                                                Adicionar Categoria
                                                <PlusCircleOutlined />
                                            </Button>
                                        </div>
                                    </PermissionWrapperComponent>

                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col span={24}>
                                    <ConfigProvider locale={ptBRIntl}>
                                        <Table
                                            rowKey="_id"
                                            columns={columns}
                                            dataSource={categories}
                                            pagination={defaultTablePagination}
                                        />
                                    </ConfigProvider>
                                </Col>
                            </Row>
                        </Content>
                </Layout>
            </Layout>
        </LayoutTopSide>
    );
}

export default AllFormCategories;
